import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

import type { TimeValue } from "@/shared/ui/dropdown"

import { toIsoDurationOrDefault } from "@/shared/lib/iso-duration"

const convertDurationToDurationString = (duration: TimeValue): string =>
  toIsoDurationOrDefault(duration.value, duration.unit)

import { TRIGGER_MODE_CONFIGS } from "./mode-config"

import { getSecurityRulesApi } from "../api/get-security-rules"
import { isPunishmentAction, isSendMessageAction, isSupportModeAction, isWholeWordFilter } from "../lib"

import type { ProhibitedWordsRule, TriggerRuleMode, UpdatePunishmentPayload } from "./types"

const initialState: ProhibitedWordsRule = {
  index: 0,
  name: "",
  filter: {
    filter_type: "or",
    filters: [
      {
        filter_type: "whole_word",
        words: [],
        case_sensitive: false
      }
    ]
  },
  actions: [
    {
      action_type: "punishment",
      parameters: {
        punishment_kind: "mute",
        duration: "PT1H",
        clean_count: 1,
        warning_threshold: null,
        warning_observation_interval: null,
        skip_notification: false
      }
    }
  ],
  mode: "blacklist",
  actionType: "punishment",
  supportModeParams: {
    force_reply: true
  }
}

export const prohibitedWordsSlice = createSlice({
  name: "prohibitedWords",
  initialState,
  reducers: {
    // Initialize rule from existing data
    setRule: (state, action: PayloadAction<ProhibitedWordsRule>) => {
      state.index = action.payload.index
      state.name = action.payload.name
      state.filter = action.payload.filter
      state.actions = action.payload.actions

      // Detect action type and mode from rule
      const firstAction = action.payload.actions.at(0)
      if (!firstAction) return

      if (isSupportModeAction(firstAction)) {
        state.mode = action.payload.mode
        state.actionType = "support_mode"
        state.supportModeParams = firstAction.parameters
      } else if (isPunishmentAction(firstAction)) {
        state.mode = "blacklist"
        state.actionType = "punishment"
      } else if (isSendMessageAction(firstAction)) {
        state.mode = "blacklist"
        state.actionType = "send_message"
      }
    },

    // Reset to initial state
    resetRule: () => initialState,

    // Initialize trigger rule with mode and optional seed data
    initializeTriggerRule: (
      state,
      action: PayloadAction<{ mode: TriggerRuleMode; seedRule?: ProhibitedWordsRule }>
    ) => {
      const { mode, seedRule } = action.payload
      const modeConfig = TRIGGER_MODE_CONFIGS[mode]
      // Always preserve the computed index from extraReducer (set by API fetch)
      const preservedIndex = state.index

      if (seedRule) {
        // Editing existing rule - copy all fields from seed, preserving computed index
        state.index = seedRule.index
        state.name = seedRule.name
        state.filter = seedRule.filter
        state.actions = seedRule.actions
        state.mode = mode
        state.actionType = seedRule.actionType
        state.supportModeParams = seedRule.supportModeParams

        // MIGRATION: Normalize name for fixed-name modes
        if (modeConfig.fixedRuleName && state.name !== modeConfig.fixedRuleName) {
          state.name = modeConfig.fixedRuleName
        }
        return
      }

      // Creating new rule - use mode config defaults
      state.name = modeConfig.fixedRuleName || initialState.name
      state.filter = initialState.filter
      state.actions = modeConfig.defaultActions
      state.mode = mode
      state.actionType = modeConfig.defaultActionType
      state.supportModeParams = initialState.supportModeParams
      state.index = preservedIndex // Use computed index from API data
    },

    // Set rule name (preserve user casing and spaces; trimming happens on save)
    setName: (state, action: PayloadAction<string>) => {
      const modeConfig = TRIGGER_MODE_CONFIGS[state.mode]
      // Block name changes for fixed-name modes
      if (modeConfig.fixedRuleName) {
        return
      }
      state.name = action.payload
    },

    addWord: (state, action: PayloadAction<string>) => {
      const word = action.payload.trim().toLowerCase()
      if (!word) return

      const firstFilter = state.filter.filters.at(0)
      if (!firstFilter || !isWholeWordFilter(firstFilter)) return

      if (firstFilter.words.includes(word)) return

      firstFilter.words.push(word)
    },

    removeWord: (state, action: PayloadAction<string>) => {
      const word = action.payload.trim().toLowerCase()
      if (!word) return

      const firstFilter = state.filter.filters.at(0)
      if (!firstFilter || !isWholeWordFilter(firstFilter)) return

      firstFilter.words = firstFilter.words.filter(w => w !== word)
    },

    updatePunishment: (state, action: PayloadAction<UpdatePunishmentPayload>) => {
      const { punishment } = action.payload
      const firstAction = state.actions.at(0)

      if (!firstAction || !isPunishmentAction(firstAction)) return

      const params = firstAction.parameters

      if (punishment.type === "warning" && punishment.warning) {
        params.punishment_kind = punishment.warning.punishmentType
        params.duration = convertDurationToDurationString(punishment.warning.punishmentDuration)
        params.warning_threshold = punishment.warning.count || 3
        params.warning_observation_interval = convertDurationToDurationString(punishment.warning.duration)
      } else {
        params.punishment_kind =
          punishment.type === "clean" ? "clean" : punishment.type === "warning" ? "mute" : punishment.type
        params.duration =
          punishment.type !== "clean" && punishment.type !== "warning"
            ? convertDurationToDurationString(punishment.duration)
            : null
        params.warning_threshold = null
        params.warning_observation_interval = null
      }

      params.clean_count = punishment.type === "clean" || punishment.deleteMessages ? 1 : null
      params.skip_notification = Boolean(punishment.skipNotification)
    },

    // Update support mode parameters
    updateSupportModeParams: (state, action: PayloadAction<{ force_reply: boolean }>) => {
      state.supportModeParams = action.payload
      // Keep state.actions in sync
      if (state.actionType === "support_mode") {
        state.actions = [
          {
            action_type: "support_mode",
            parameters: state.supportModeParams
          }
        ]
      }
    },

    // Clear words
    clearWords: state => {
      const firstFilter = state.filter.filters.at(0)
      if (!firstFilter || !isWholeWordFilter(firstFilter)) return

      firstFilter.words = []
    }
  },
  extraReducers: builder => {
    // When security rules are fetched successfully, calculate the next index
    builder.addMatcher(getSecurityRulesApi.endpoints.getSecurityRulesByChatId.matchFulfilled, (state, action) => {
      if (state.mode !== "blacklist") return
      // Only update index if we're creating a new rule (name is empty and index is 0)
      // if (state.name === "" && state.index === 0) {
      const rules = action.payload
      const triggerWordsRule = rules.find(rule => rule.kind === "trigger_words")

      if (triggerWordsRule && triggerWordsRule.config.rules.length > 0) {
        // Find the maximum index from existing rules
        const maxIndex = triggerWordsRule.config.rules.reduce((max, rule) => Math.max(max, rule.index), -1)
        // Set new index to be one more than the maximum
        state.index = maxIndex + 1
      } else {
        // If no rules exist, start with index 0
        state.index = 0
      }
      // }
    })
  }
})

// Export actions
export const {
  resetRule,
  initializeTriggerRule,
  setName,
  addWord,
  removeWord,
  updatePunishment,
  clearWords,
  updateSupportModeParams
} = prohibitedWordsSlice.actions

// Export reducer
export const prohibitedWordsReducer = prohibitedWordsSlice.reducer
